import { AlgComponentType } from '@youshift/shared/types';

export const UNBREAKABLE_LEVEL_VALUE = -1;

export enum ImportanceLevel {
  VERY_LOW = 'VERY_LOW',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY_HIGH',
  UNBREAKABLE = 'UNBREAKABLE',
}

export const IMPORTANCE_LEVEL_MAX_VALUES: Record<ImportanceLevel, number> = {
  [ImportanceLevel.VERY_LOW]: 10,
  [ImportanceLevel.LOW]: 20,
  [ImportanceLevel.MEDIUM]: 50,
  [ImportanceLevel.HIGH]: 100,
  [ImportanceLevel.VERY_HIGH]: 500,
  [ImportanceLevel.UNBREAKABLE]: -1,
};

export interface ImportanceBracket {
  level: ImportanceLevel;
  maxValue: number;
  minValue: number;
  label: string;
  color: string;
  bgColor: string;
}

export const IMPORTANCE_BRACKETS: Record<ImportanceLevel, ImportanceBracket> = {
  [ImportanceLevel.VERY_LOW]: {
    level: ImportanceLevel.VERY_LOW,
    maxValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.VERY_LOW],
    minValue: 0,
    label: `manager.smartass.algorithmSettingsModal.priorityLevels.${ImportanceLevel.VERY_LOW}`,
    color: 'text-blue-700',
    bgColor: 'bg-gradient-to-r from-blue-50 to-blue-100',
  },
  [ImportanceLevel.LOW]: {
    level: ImportanceLevel.LOW,
    maxValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.LOW],
    minValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.VERY_LOW] + 1,
    label: `manager.smartass.algorithmSettingsModal.priorityLevels.${ImportanceLevel.LOW}`,
    color: 'text-green-700',
    bgColor: 'bg-gradient-to-r from-green-50 to-green-100',
  },
  [ImportanceLevel.MEDIUM]: {
    level: ImportanceLevel.MEDIUM,
    maxValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.MEDIUM],
    minValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.LOW] + 1,
    label: `manager.smartass.algorithmSettingsModal.priorityLevels.${ImportanceLevel.MEDIUM}`,
    color: 'text-yellow-700',
    bgColor: 'bg-gradient-to-r from-yellow-50 to-yellow-100',
  },
  [ImportanceLevel.HIGH]: {
    level: ImportanceLevel.HIGH,
    maxValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.HIGH],
    minValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.MEDIUM] + 1,
    label: `manager.smartass.algorithmSettingsModal.priorityLevels.${ImportanceLevel.HIGH}`,
    color: 'text-orange-700',
    bgColor: 'bg-gradient-to-r from-orange-50 to-orange-100',
  },
  [ImportanceLevel.VERY_HIGH]: {
    level: ImportanceLevel.VERY_HIGH,
    maxValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.VERY_HIGH],
    minValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.HIGH] + 1,
    label: `manager.smartass.algorithmSettingsModal.priorityLevels.${ImportanceLevel.VERY_HIGH}`,
    color: 'text-red-700',
    bgColor: 'bg-gradient-to-r from-red-50 to-red-100',
  },
  [ImportanceLevel.UNBREAKABLE]: {
    level: ImportanceLevel.UNBREAKABLE,
    maxValue: -1,
    minValue: IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.VERY_HIGH] + 1,
    label: `manager.smartass.algorithmSettingsModal.priorityLevels.${ImportanceLevel.UNBREAKABLE}`,
    color: 'text-purple-50 font-bold',
    bgColor: 'bg-gradient-to-r from-indigo-600 to-purple-600',
  },
};

export interface AlgorithmSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface ImportanceLevelListboxProps {
  value: number;
  onChange: (level: ImportanceLevel) => void;
  className?: string;
}

export interface ImportanceValueInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  isUnbreakable?: boolean;
}

// List of components that support priority
// For example, C_1 can never be deprioritized, so it is not included
export type AlgComponentTypeSupportingPriority =
  | AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN
  | AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX
  | AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MIN
  | AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MAX
  | AlgComponentType.CROSS_GROUP_INCOMPATIBILITY
  | AlgComponentType.C_PERSONAL_BLOCKING
  | AlgComponentType.C_JUSTIFIED_BLOCKING
  | AlgComponentType.C_SPECIAL_EVENTS
  | AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS
  | AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_SLOTS
  | AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_SLOTS
  | AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_DURATION
  | AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_DURATION
  | AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_COUNTER_UNIT
  | AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_COUNTER_UNIT;
